import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  FiUsers,
  FiSearch,
  FiPlus,
  FiRefreshCcw,
  FiEdit,
  FiTrash2,
  FiEye,
  FiDownload,
  FiUpload,
  FiCheckSquare,
  FiSquare,
  FiToggleLeft,
  FiToggleRight,
  FiFilter,
} from "react-icons/fi";
import { fetchUsers } from "../../store/slices/adminSlice";
import adminApi from "../../api/adminApi";
import Table from "../../components/Table/Table";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/Button/Button";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import "./Users.css";

const ROLE_OPTIONS = [
  { value: "student", label: "Student" },
  { value: "mentor", label: "Mentor" },
  { value: "batch manager", label: "Batch Manager" },
  { value: "doubt solver", label: "Doubt Solver" },
  { value: "admin", label: "Admin" },
  { value: "superadmin", label: "Super Admin" },
];

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { users, isLoading, error } = useSelector((state) => state.admin);
  const { user: currentUser } = useSelector((state) => state.auth);

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkActionsModal, setShowBulkActionsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [page, roleFilter, statusFilter, emailVerifiedFilter]);

  // Debounce search
  useEffect(() => {
    if (!searchQuery) {
      if (page !== 1) {
        setPage(1);
      } else {
        loadUsers();
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      if (page === 1) {
        loadUsers();
      } else {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const loadUsers = async () => {
    const params = {
      page,
      limit: 25,
      search: searchQuery || undefined,
    };
    if (roleFilter !== "all") {
      params.role = roleFilter;
    }
    dispatch(fetchUsers(params));
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setDeleteLoading(true);
    try {
      await adminApi.delete(`/users/${selectedUser._id}`);
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminApi.patch(`/users/${userId}/role`, { role: newRole });
      loadUsers();
    } catch (err) {
      console.error("Role change failed:", err);
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      // Update user status - backend may have an endpoint for this
      // For now, we'll use a patch to update a status field if it exists
      await adminApi.patch(`/users/${userId}`, {
        isActive: !isActive,
        active: !isActive,
      });
      loadUsers();
    } catch (err) {
      console.error("Status toggle failed:", err);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.size === 0) return;

    setBulkActionLoading(true);
    try {
      const userIds = Array.from(selectedUsers);

      if (action === "delete") {
        await Promise.all(
          userIds.map((id) => adminApi.delete(`/users/${id}`))
        );
      } else if (action === "activate") {
        await Promise.all(
          userIds.map((id) =>
            adminApi.patch(`/users/${id}`, {
              isActive: true,
              active: true,
            })
          )
        );
      } else if (action === "deactivate") {
        await Promise.all(
          userIds.map((id) =>
            adminApi.patch(`/users/${id}`, {
              isActive: false,
              active: false,
            })
          )
        );
      } else if (action.startsWith("role:")) {
        const newRole = action.split(":")[1];
        await Promise.all(
          userIds.map((id) =>
            adminApi.patch(`/users/${id}/role`, { role: newRole })
          )
        );
      }

      setSelectedUsers(new Set());
      setShowBulkActionsModal(false);
      loadUsers();
    } catch (err) {
      console.error("Bulk action failed:", err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    const usersList = users?.users || users?.data || [];
    if (selectedUsers.size === usersList.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(usersList.map((u) => u._id)));
    }
  };

  const handleExport = () => {
    const usersList = users?.users || users?.data || [];
    const csvContent = [
      [
        "Name",
        "Email",
        "Phone",
        "Role",
        "Status",
        "Email Verified",
        "Joined Date",
      ].join(","),
      ...usersList.map((user) =>
        [
          `"${user.firstName} ${user.lastName}"`,
          user.email,
          user.phone || user.mobileNumber || "",
          user.role,
          user.isActive !== false ? "Active" : "Inactive",
          user.emailVerified ? "Yes" : "No",
          user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const text = await file.text();
      const lines = text.split("\n").filter((line) => line.trim());
      const headers = lines[0]
        .split(",")
        .map((h) => h.trim().replace(/"/g, ""));

      // Skip header row
      const data = lines.slice(1).map((line) => {
        const values = line.split(",").map((v) => v.trim().replace(/"/g, ""));
        const user = {};
        headers.forEach((header, index) => {
          user[header.toLowerCase().replace(/\s+/g, "")] = values[index];
        });
        return user;
      });

      // Create users in bulk
      // Note: This is a simplified import - you may need to adjust based on backend
      for (const userData of data) {
        try {
          await adminApi.post("/users", {
            firstName: userData.firstname || userData.name?.split(" ")[0],
            lastName:
              userData.lastname || userData.name?.split(" ").slice(1).join(" "),
            email: userData.email,
            mobileNumber: userData.phone || userData.mobilenumber,
            role: userData.role || "student",
            password: "TempPassword123!", // Should be changed on first login
            dateOfBirth: userData.dateofbirth || userData.dob,
            gender: userData.gender || "Other",
            address: userData.address || "",
          });
        } catch (err) {
          console.error("Failed to import user:", userData.email, err);
        }
      }

      setShowImportModal(false);
      loadUsers();
    } catch (err) {
      console.error("Import failed:", err);
    } finally {
      setImportLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const usersList = users?.users || users?.data || [];
    if (!Array.isArray(usersList)) return [];
    const query = searchQuery.toLowerCase();
    return usersList.filter((user) => {
      const matchesSearch =
        !query ||
        [
          user.firstName,
          user.lastName,
          user.email,
          user.phone,
          user.mobileNumber,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query));

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && user.isActive !== false) ||
        (statusFilter === "inactive" && user.isActive === false);

      const matchesEmailVerified =
        emailVerifiedFilter === "all" ||
        (emailVerifiedFilter === "verified" && user.emailVerified) ||
        (emailVerifiedFilter === "unverified" && !user.emailVerified);

      return matchesSearch && matchesStatus && matchesEmailVerified;
    });
  }, [users, searchQuery, statusFilter, emailVerifiedFilter]);

  const columns = [
    {
      key: "select",
      label: (
        <button
          type="button"
          className="select-all-btn"
          onClick={handleSelectAll}
          title="Select all"
        >
          {selectedUsers.size > 0 &&
          selectedUsers.size === (users?.users || users?.data || []).length ? (
            <FiCheckSquare size={18} />
          ) : (
            <FiSquare size={18} />
          )}
        </button>
      ),
      render: (row) => (
        <button
          type="button"
          className="select-user-btn"
          onClick={() => handleSelectUser(row._id)}
        >
          {selectedUsers.has(row._id) ? (
            <FiCheckSquare size={18} />
          ) : (
            <FiSquare size={18} />
          )}
        </button>
      ),
    },
    {
      key: "name",
      label: "User",
      render: (row) => (
        <div className="user-cell">
          <div className="user-avatar">
            {row.firstName?.[0]}
            {row.lastName?.[0]}
          </div>
          <div>
            <strong>
              {row.firstName} {row.lastName}
            </strong>
            <span className="user-email">{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      label: "Role",
      render: (row) => (
        <select
          value={row.role}
          onChange={(e) => handleRoleChange(row._id, e.target.value)}
          disabled={
            currentUser?.role !== "superadmin" || row.role === "superadmin"
          }
          className="role-select"
        >
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "phone",
      label: "Contact",
      render: (row) => row.phone || "—",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <div className="status-cell">
          <StatusBadge status={row.emailVerified ? "active" : "pending"} />
          <button
            type="button"
            className="toggle-status-btn"
            onClick={() => handleToggleActive(row._id, row.isActive !== false)}
            title={row.isActive !== false ? "Deactivate" : "Activate"}
          >
            {row.isActive !== false ? (
              <FiToggleRight size={20} className="active" />
            ) : (
              <FiToggleLeft size={20} className="inactive" />
            )}
          </button>
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="user-actions">
          <Link to={`/users/${row._id}`} className="action-link">
            View Details
          </Link>
          <button
            type="button"
            className="action-btn delete"
            onClick={() => {
              setSelectedUser(row);
              setShowDeleteModal(true);
            }}
            disabled={
              row.role === "superadmin" && currentUser?.role !== "superadmin"
            }
            title="Delete user"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const stats = useMemo(() => {
    const usersList = users?.users || users?.data || [];
    if (!Array.isArray(usersList)) return [];
    const total = users?.total || usersList.length;
    const byRole = usersList.reduce((acc, user) => {
      acc[user.role] = (acc[user.role] || 0) + 1;
      return acc;
    }, {});
    return [
      { label: "Total Users", value: total, icon: FiUsers },
      { label: "Students", value: byRole.student || 0 },
      {
        label: "Staff",
        value:
          (byRole.mentor || 0) +
          (byRole["batch manager"] || 0) +
          (byRole["doubt solver"] || 0),
      },
      {
        label: "Admins",
        value: (byRole.admin || 0) + (byRole.superadmin || 0),
      },
    ];
  }, [users]);

  return (
    <div className="users-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">User Management</p>
          <h1>All Users</h1>
          <p className="subhead">
            Manage students, teachers, and staff members
          </p>
        </div>
        <div className="header-actions">
          <Button onClick={() => navigate("/users/new")}>
            <FiPlus size={18} />
            Add User
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            <FiDownload size={18} />
            Export
          </Button>
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            <FiUpload size={18} />
            Import
          </Button>
          {selectedUsers.size > 0 && (
            <Button
              variant="secondary"
              onClick={() => setShowBulkActionsModal(true)}
            >
              Bulk Actions ({selectedUsers.size})
            </Button>
          )}
          <button
            type="button"
            className="refresh-btn"
            onClick={loadUsers}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </header>

      <section className="stats-grid">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article key={stat.label} className="stat-card">
              {Icon && (
                <span className="stat-icon">
                  <Icon size={20} />
                </span>
              )}
              <div>
                <p>{stat.label}</p>
                <strong>{stat.value}</strong>
              </div>
            </article>
          );
        })}
      </section>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search by name, email, or phone"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setPage(1);
                loadUsers();
              }
            }}
          />
        </div>
        <Select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
          options={[{ value: "all", label: "All Roles" }, ...ROLE_OPTIONS]}
        />
        <Button
          variant="secondary"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
        >
          <FiFilter size={18} />
          Filters
        </Button>
      </section>

      {showAdvancedFilters && (
        <section className="advanced-filters">
          <div className="filter-group">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: "all", label: "All Status" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
            <Select
              label="Email Verified"
              value={emailVerifiedFilter}
              onChange={(e) => {
                setEmailVerifiedFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: "all", label: "All" },
                { value: "verified", label: "Verified" },
                { value: "unverified", label: "Unverified" },
              ]}
            />
          </div>
        </section>
      )}

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredUsers}
        loading={isLoading}
        emptyMessage="No users found"
      />

      {(users?.pagination || users?.totalPages) && (
        <div className="pagination">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of{" "}
            {users?.pagination?.totalPages || users?.totalPages || 1}
          </span>
          <button
            type="button"
            disabled={
              page >= (users?.pagination?.totalPages || users?.totalPages || 1)
            }
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        title="Delete User"
        size="small"
      >
        <p>
          Are you sure you want to delete {selectedUser?.firstName}{" "}
          {selectedUser?.lastName}?
        </p>
        <p className="warning-text">This action cannot be undone.</p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {/* Bulk Actions Modal */}
      <Modal
        isOpen={showBulkActionsModal}
        onClose={() => {
          setShowBulkActionsModal(false);
        }}
        title={`Bulk Actions (${selectedUsers.size} users)`}
        size="medium"
      >
        <div className="bulk-actions-list">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => handleBulkAction("activate")}
            loading={bulkActionLoading}
          >
            Activate Selected
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => handleBulkAction("deactivate")}
            loading={bulkActionLoading}
          >
            Deactivate Selected
          </Button>
          <Select
            label="Change Role"
            onChange={(e) => {
              if (e.target.value) {
                handleBulkAction(`role:${e.target.value}`);
              }
            }}
            options={[
              { value: "", label: "Select role..." },
              ...ROLE_OPTIONS.filter((opt) => opt.value !== "superadmin"),
            ]}
          />
          <Button
            variant="danger"
            fullWidth
            onClick={() => {
              if (
                window.confirm(
                  `Delete ${selectedUsers.size} users? This cannot be undone.`
                )
              ) {
                handleBulkAction("delete");
              }
            }}
            loading={bulkActionLoading}
          >
            Delete Selected
          </Button>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Users from CSV"
        size="medium"
      >
        <div className="import-instructions">
          <p>Upload a CSV file with the following columns:</p>
          <ul>
            <li>Name (or FirstName, LastName)</li>
            <li>Email</li>
            <li>Phone (or MobileNumber)</li>
            <li>Role (optional, defaults to student)</li>
            <li>DateOfBirth (optional)</li>
            <li>Gender (optional)</li>
            <li>Address (optional)</li>
          </ul>
        </div>
        <div className="import-actions">
          <input
            type="file"
            accept=".csv"
            onChange={handleImport}
            disabled={importLoading}
            id="csv-import"
            style={{ display: "none" }}
          />
          <label htmlFor="csv-import">
            <Button variant="secondary" as="span" loading={importLoading}>
              <FiUpload size={18} />
              Choose CSV File
            </Button>
          </label>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
